<template>
    <v-dialog v-model="visible" fullscreen scrollable transition="dialog-bottom-transition">
        <v-card class="analytics-card">
            <!-- Header -->
            <div class="card-header">
                <div class="left-section">
                    <div class="header-badge">{{ $t('analyticsDialog.header.badge') }}</div>
                    <h2>{{ $t('analyticsDialog.header.title') }}</h2>
                    <p class="header-description">
                        {{ $t('analyticsDialog.header.description') }}
                    </p>
                </div>
                <div class="right-section">
                    <button @click="closeDialog" class="dialog-close-btn">
                        <v-icon>mdi-close</v-icon>
                    </button>
                </div>
            </div>

            <!-- Main Content -->
            <div class="analytics-container">
                <!-- Key Metrics Grid -->
                <div class="metrics-grid">
                    <div class="metric-card energy">
                        <div class="metric-header">
                            <div class="metric-icon">
                                <v-icon size="28" color="white">mdi-flash</v-icon>
                            </div>
                            <div class="metric-badge decrease">{{ $t('analyticsDialog.metrics.energy.badge') }}</div>
                        </div>
                        <div class="metric-body">
                            <h4>{{ $t('analyticsDialog.metrics.energy.title') }}</h4>
                            <p class="metric-value">{{ $t('analyticsDialog.metrics.energy.value') }}</p>
                            <p class="metric-label">{{ $t('analyticsDialog.metrics.energy.label') }}</p>
                        </div>
                        <div class="metric-footer">
                            <span class="trend-text">{{ $t('analyticsDialog.metrics.energy.trend') }}</span>
                        </div>
                    </div>

                    <div class="metric-card occupancy">
                        <div class="metric-header">
                            <div class="metric-icon">
                                <v-icon size="28" color="white">mdi-account-group</v-icon>
                            </div>
                            <div class="metric-badge increase">{{ $t('analyticsDialog.metrics.occupancy.badge') }}</div>
                        </div>
                        <div class="metric-body">
                            <h4>{{ $t('analyticsDialog.metrics.occupancy.title') }}</h4>
                            <p class="metric-value">{{ $t('analyticsDialog.metrics.occupancy.value') }}</p>
                            <p class="metric-label">{{ $t('analyticsDialog.metrics.occupancy.label') }}</p>
                        </div>
                        <div class="metric-footer">
                            <span class="trend-text">{{ $t('analyticsDialog.metrics.occupancy.trend') }}</span>
                        </div>
                    </div>

                    <div class="metric-card utilization">
                        <div class="metric-header">
                            <div class="metric-icon">
                                <v-icon size="28" color="white">mdi-desk</v-icon>
                            </div>
                            <div class="metric-badge increase">{{ $t('analyticsDialog.metrics.utilization.badge') }}</div>
                        </div>
                        <div class="metric-body">
                            <h4>{{ $t('analyticsDialog.metrics.utilization.title') }}</h4>
                            <p class="metric-value">{{ $t('analyticsDialog.metrics.utilization.value') }}</p>
                            <p class="metric-label">{{ $t('analyticsDialog.metrics.utilization.label') }}</p>
                        </div>
                        <div class="metric-footer">
                            <span class="trend-text">{{ $t('analyticsDialog.metrics.utilization.trend') }}</span>
                        </div>
                    </div>

                    <div class="metric-card satisfaction">
                        <div class="metric-header">
                            <div class="metric-icon">
                                <v-icon size="28" color="white">mdi-star</v-icon>
                            </div>
                            <div class="metric-badge increase">{{ $t('analyticsDialog.metrics.satisfaction.badge') }}</div>
                        </div>
                        <div class="metric-body">
                            <h4>{{ $t('analyticsDialog.metrics.satisfaction.title') }}</h4>
                            <p class="metric-value">{{ $t('analyticsDialog.metrics.satisfaction.value') }}</p>
                            <p class="metric-label">{{ $t('analyticsDialog.metrics.satisfaction.label') }}</p>
                        </div>
                        <div class="metric-footer">
                            <span class="trend-text">{{ $t('analyticsDialog.metrics.satisfaction.trend') }}</span>
                        </div>
                    </div>

                    <div class="metric-card cost">
                        <div class="metric-header">
                            <div class="metric-icon">
                                <v-icon size="28" color="white">mdi-currency-eur</v-icon>
                            </div>
                            <div class="metric-badge decrease">{{ $t('analyticsDialog.metrics.cost.badge') }}</div>
                        </div>
                        <div class="metric-body">
                            <h4>{{ $t('analyticsDialog.metrics.cost.title') }}</h4>
                            <p class="metric-value">{{ $t('analyticsDialog.metrics.cost.value') }}</p>
                            <p class="metric-label">{{ $t('analyticsDialog.metrics.cost.label') }}</p>
                        </div>
                        <div class="metric-footer">
                            <span class="trend-text">{{ $t('analyticsDialog.metrics.cost.trend') }}</span>
                        </div>
                    </div>

                    <div class="metric-card efficiency">
                        <div class="metric-header">
                            <div class="metric-icon">
                                <v-icon size="28" color="white">mdi-speedometer</v-icon>
                            </div>
                            <div class="metric-badge increase">{{ $t('analyticsDialog.metrics.efficiency.badge') }}</div>
                        </div>
                        <div class="metric-body">
                            <h4>{{ $t('analyticsDialog.metrics.efficiency.title') }}</h4>
                            <p class="metric-value">{{ $t('analyticsDialog.metrics.efficiency.value') }}</p>
                            <p class="metric-label">{{ $t('analyticsDialog.metrics.efficiency.label') }}</p>
                        </div>
                        <div class="metric-footer">
                            <span class="trend-text">{{ $t('analyticsDialog.metrics.efficiency.trend') }}</span>
                        </div>
                    </div>
                </div>

                <!-- Analytics Categories -->
                <div class="categories-section">
                    <div class="section-header">
                        <h3>{{ $t('analyticsDialog.categories.title') }}</h3>
                        <p>{{ $t('analyticsDialog.categories.subtitle') }}</p>
                    </div>

                    <v-row class="categories-grid">
                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="category-card space-analytics">
                                <div class="category-icon">
                                    <v-icon size="32" color="white">mdi-floor-plan</v-icon>
                                </div>
                                <h4>{{ $t('analyticsDialog.categories.spaceAnalytics.title') }}</h4>
                                <p>{{ $t('analyticsDialog.categories.spaceAnalytics.description') }}</p>
                                <div class="category-features">
                                    <div 
                                        v-for="(tag, index) in spaceAnalyticsTags" 
                                        :key="`space-${index}`" 
                                        class="feature-tag"
                                    >
                                        {{ tag }}
                                    </div>
                                </div>
                            </div>
                        </v-col>

                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="category-card energy-analytics">
                                <div class="category-icon">
                                    <v-icon size="32" color="white">mdi-lightning-bolt</v-icon>
                                </div>
                                <h4>{{ $t('analyticsDialog.categories.energyManagement.title') }}</h4>
                                <p>{{ $t('analyticsDialog.categories.energyManagement.description') }}</p>
                                <div class="category-features">
                                    <div 
                                        v-for="(tag, index) in energyManagementTags" 
                                        :key="`energy-${index}`" 
                                        class="feature-tag"
                                    >
                                        {{ tag }}
                                    </div>
                                </div>
                            </div>
                        </v-col>

                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="category-card user-analytics">
                                <div class="category-icon">
                                    <v-icon size="32" color="white">mdi-account-analytics</v-icon>
                                </div>
                                <h4>{{ $t('analyticsDialog.categories.userBehavior.title') }}</h4>
                                <p>{{ $t('analyticsDialog.categories.userBehavior.description') }}</p>
                                <div class="category-features">
                                    <div 
                                        v-for="(tag, index) in userBehaviorTags" 
                                        :key="`user-${index}`" 
                                        class="feature-tag"
                                    >
                                        {{ tag }}
                                    </div>
                                </div>
                            </div>
                        </v-col>

                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="category-card booking-analytics">
                                <div class="category-icon">
                                    <v-icon size="32" color="white">mdi-calendar-clock</v-icon>
                                </div>
                                <h4>{{ $t('analyticsDialog.categories.bookingTrends.title') }}</h4>
                                <p>{{ $t('analyticsDialog.categories.bookingTrends.description') }}</p>
                                <div class="category-features">
                                    <div 
                                        v-for="(tag, index) in bookingTrendsTags" 
                                        :key="`booking-${index}`" 
                                        class="feature-tag"
                                    >
                                        {{ tag }}
                                    </div>
                                </div>
                            </div>
                        </v-col>

                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="category-card environmental">
                                <div class="category-icon">
                                    <v-icon size="32" color="white">mdi-leaf</v-icon>
                                </div>
                                <h4>{{ $t('analyticsDialog.categories.environmental.title') }}</h4>
                                <p>{{ $t('analyticsDialog.categories.environmental.description') }}</p>
                                <div class="category-features">
                                    <div 
                                        v-for="(tag, index) in environmentalTags" 
                                        :key="`env-${index}`" 
                                        class="feature-tag"
                                    >
                                        {{ tag }}
                                    </div>
                                </div>
                            </div>
                        </v-col>

                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="category-card predictive">
                                <div class="category-icon">
                                    <v-icon size="32" color="white">mdi-crystal-ball</v-icon>
                                </div>
                                <h4>{{ $t('analyticsDialog.categories.predictive.title') }}</h4>
                                <p>{{ $t('analyticsDialog.categories.predictive.description') }}</p>
                                <div class="category-features">
                                    <div 
                                        v-for="(tag, index) in predictiveTags" 
                                        :key="`pred-${index}`" 
                                        class="feature-tag"
                                    >
                                        {{ tag }}
                                    </div>
                                </div>
                            </div>
                        </v-col>
                    </v-row>
                </div>

                <!-- Charts Section -->
                <div class="charts-section">
                    <v-row>
                        <!-- Occupancy Trends -->
                        <v-col cols="12" lg="6" class="pa-3">
                            <div class="chart-container">
                                <div class="chart-header">
                                    <h4>{{ $t('analyticsDialog.charts.occupancyTrends.title') }}</h4>
                                    <p>{{ $t('analyticsDialog.charts.occupancyTrends.subtitle') }}</p>
                                </div>
                                <div class="chart-wrapper">
                                    <apexchart 
                                        type="line" 
                                        height="300" 
                                        :options="occupancyLineOptions" 
                                        :series="occupancyLineSeries" 
                                    />
                                </div>
                            </div>
                        </v-col>

                        <!-- Energy Consumption -->
                        <v-col cols="12" lg="6" class="pa-3">
                            <div class="chart-container">
                                <div class="chart-header">
                                    <h4>{{ $t('analyticsDialog.charts.energyConsumption.title') }}</h4>
                                    <p>{{ $t('analyticsDialog.charts.energyConsumption.subtitle') }}</p>
                                </div>
                                <div class="chart-wrapper">
                                    <apexchart 
                                        type="bar" 
                                        height="300" 
                                        :options="energyBarOptions" 
                                        :series="energyBarSeries" 
                                    />
                                </div>
                            </div>
                        </v-col>

                        <!-- Space Utilization -->
                        <v-col cols="12" lg="6" class="pa-3">
                            <div class="chart-container">
                                <div class="chart-header">
                                    <h4>{{ $t('analyticsDialog.charts.spaceUtilization.title') }}</h4>
                                    <p>{{ $t('analyticsDialog.charts.spaceUtilization.subtitle') }}</p>
                                </div>
                                <div class="chart-wrapper">
                                    <apexchart 
                                        type="bar" 
                                        height="300" 
                                        :options="spaceUtilizationOptions" 
                                        :series="spaceUtilizationSeries" 
                                    />
                                </div>
                            </div>
                        </v-col>

                        <!-- Peak Hours -->
                        <v-col cols="12" lg="6" class="pa-3">
                            <div class="chart-container">
                                <div class="chart-header">
                                    <h4>{{ $t('analyticsDialog.charts.peakHours.title') }}</h4>
                                    <p>{{ $t('analyticsDialog.charts.peakHours.subtitle') }}</p>
                                </div>
                                <div class="chart-wrapper">
                                    <apexchart 
                                        type="area" 
                                        height="300" 
                                        :options="peakHoursOptions" 
                                        :series="peakHoursSeries" 
                                    />
                                </div>
                            </div>
                        </v-col>
                    </v-row>
                </div>

                <!-- Key Insights -->
                <div class="insights-section">
                    <div class="section-header">
                        <h3>{{ $t('analyticsDialog.insights.title') }}</h3>
                        <p>{{ $t('analyticsDialog.insights.subtitle') }}</p>
                    </div>

                    <div class="insights-grid">
                        <div class="insight-card priority-high">
                            <div class="insight-icon">
                                <v-icon size="24" color="white">mdi-lightbulb-on</v-icon>
                            </div>
                            <h4>{{ $t('analyticsDialog.insights.insight1.title') }}</h4>
                            <p>{{ $t('analyticsDialog.insights.insight1.description') }}</p>
                        </div>

                        <div class="insight-card priority-medium">
                            <div class="insight-icon">
                                <v-icon size="24" color="white">mdi-lightbulb-on</v-icon>
                            </div>
                            <h4>{{ $t('analyticsDialog.insights.insight2.title') }}</h4>
                            <p>{{ $t('analyticsDialog.insights.insight2.description') }}</p>
                        </div>

                        <div class="insight-card priority-medium">
                            <div class="insight-icon">
                                <v-icon size="24" color="white">mdi-lightbulb-on</v-icon>
                            </div>
                            <h4>{{ $t('analyticsDialog.insights.insight3.title') }}</h4>
                            <p>{{ $t('analyticsDialog.insights.insight3.description') }}</p>
                        </div>

                        <div class="insight-card priority-low">
                            <div class="insight-icon">
                                <v-icon size="24" color="white">mdi-lightbulb-on</v-icon>
                            </div>
                            <h4>{{ $t('analyticsDialog.insights.insight4.title') }}</h4>
                            <p>{{ $t('analyticsDialog.insights.insight4.description') }}</p>
                        </div>
                    </div>
                </div>

                <!-- Industry Benchmarking -->
                <div class="comparison-section">
                    <div class="section-header">
                        <h3>{{ $t('analyticsDialog.comparison.title') }}</h3>
                        <p>{{ $t('analyticsDialog.comparison.subtitle') }}</p>
                    </div>

                    <div class="comparison-cards">
                        <div class="comparison-card">
                            <h4>{{ $t('analyticsDialog.comparison.metrics.energyEfficiency') }}</h4>
                            <div class="comparison-bars">
                                <div class="bar-group">
                                    <div class="bar-label">{{ $t('analyticsDialog.comparison.yourBuilding') }}</div>
                                    <div class="bar-container">
                                        <div class="bar yours" style="width: 94%;">94%</div>
                                    </div>
                                </div>
                                <div class="bar-group">
                                    <div class="bar-label">{{ $t('analyticsDialog.comparison.industryAverage') }}</div>
                                    <div class="bar-container">
                                        <div class="bar industry" style="width: 78%;">78%</div>
                                    </div>
                                </div>
                            </div>
                            <div class="comparison-legend">
                                <span><div class="legend-dot yours"></div>{{ $t('analyticsDialog.comparison.yourBuilding') }}</span>
                                <span><div class="legend-dot industry"></div>{{ $t('analyticsDialog.comparison.industryAverage') }}</span>
                            </div>
                        </div>

                        <div class="comparison-card">
                            <h4>{{ $t('analyticsDialog.comparison.metrics.spaceUtilization') }}</h4>
                            <div class="comparison-bars">
                                <div class="bar-group">
                                    <div class="bar-label">{{ $t('analyticsDialog.comparison.yourBuilding') }}</div>
                                    <div class="bar-container">
                                        <div class="bar yours" style="width: 82%;">82%</div>
                                    </div>
                                </div>
                                <div class="bar-group">
                                    <div class="bar-label">{{ $t('analyticsDialog.comparison.industryAverage') }}</div>
                                    <div class="bar-container">
                                        <div class="bar industry" style="width: 75%;">75%</div>
                                    </div>
                                </div>
                            </div>
                            <div class="comparison-legend">
                                <span><div class="legend-dot yours"></div>{{ $t('analyticsDialog.comparison.yourBuilding') }}</span>
                                <span><div class="legend-dot industry"></div>{{ $t('analyticsDialog.comparison.industryAverage') }}</span>
                            </div>
                        </div>

                        <div class="comparison-card">
                            <h4>{{ $t('analyticsDialog.comparison.quarters.title') }}</h4>
                            <div class="quarter-comparison">
                                <div class="quarter-metric">
                                    <div class="quarter-icon improvement">
                                        <v-icon size="24">mdi-trending-up</v-icon>
                                    </div>
                                    <div class="quarter-details">
                                        <span class="quarter-label">{{ $t('analyticsDialog.comparison.quarters.energySavings') }}</span>
                                        <span class="quarter-value">€12,450</span>
                                        <span class="quarter-percent">+18% vs Q3</span>
                                    </div>
                                </div>

                                <div class="quarter-metric">
                                    <div class="quarter-icon improvement">
                                        <v-icon size="24">mdi-trending-up</v-icon>
                                    </div>
                                    <div class="quarter-details">
                                        <span class="quarter-label">{{ $t('analyticsDialog.comparison.quarters.costReduction') }}</span>
                                        <span class="quarter-value">€8,320</span>
                                        <span class="quarter-percent">+15% vs Q3</span>
                                    </div>
                                </div>

                                <div class="quarter-metric">
                                    <div class="quarter-icon improvement">
                                        <v-icon size="24">mdi-trending-up</v-icon>
                                    </div>
                                    <div class="quarter-details">
                                        <span class="quarter-label">{{ $t('analyticsDialog.comparison.quarters.satisfactionIncrease') }}</span>
                                        <span class="quarter-value">+0.4</span>
                                        <span class="quarter-percent">+9% vs Q3</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </v-card>
    </v-dialog>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

// Dialog visibility
const visible = ref(false);

// Computed properties for category tags - properly access arrays
const spaceAnalyticsTags = computed(() => {
    const tags = t('analyticsDialog.categories.spaceAnalytics.tags');
    return Array.isArray(tags) ? tags : [];
});
const energyManagementTags = computed(() => {
    const tags = t('analyticsDialog.categories.energyManagement.tags');
    return Array.isArray(tags) ? tags : [];
});
const userBehaviorTags = computed(() => {
    const tags = t('analyticsDialog.categories.userBehavior.tags');
    return Array.isArray(tags) ? tags : [];
});
const bookingTrendsTags = computed(() => {
    const tags = t('analyticsDialog.categories.bookingTrends.tags');
    return Array.isArray(tags) ? tags : [];
});
const environmentalTags = computed(() => {
    const tags = t('analyticsDialog.categories.environmental.tags');
    return Array.isArray(tags) ? tags : [];
});
const predictiveTags = computed(() => {
    const tags = t('analyticsDialog.categories.predictive.tags');
    return Array.isArray(tags) ? tags : [];
});

// Occupancy Line Chart
const occupancyLineSeries = ref([{
    name: t('analyticsDialog.chartLabels.occupancyPercent'),
    data: [65, 72, 68, 78, 82, 85, 79]
}]);

const occupancyLineOptions = computed(() => ({
    chart: {
        type: 'line',
        fontFamily: 'Inter, sans-serif',
        toolbar: { show: false }
    },
    colors: ['#1E5A8E'],
    stroke: {
        curve: 'smooth',
        width: 3
    },
    xaxis: {
        categories: [
            t('analyticsDialog.weekdays.mon'),
            t('analyticsDialog.weekdays.tue'),
            t('analyticsDialog.weekdays.wed'),
            t('analyticsDialog.weekdays.thu'),
            t('analyticsDialog.weekdays.fri'),
            t('analyticsDialog.weekdays.sat'),
            t('analyticsDialog.weekdays.sun')
        ]
    },
    yaxis: {
        title: { text: t('analyticsDialog.chartLabels.occupancyPercent') }
    },
    grid: { borderColor: '#f3f4f6' }
}));

// Energy Bar Chart
const energyBarSeries = ref([{
    name: t('analyticsDialog.chartLabels.energyKwh'),
    data: [14200, 13800, 15100, 14500, 13200, 12847]
}]);

const energyBarOptions = computed(() => ({
    chart: {
        type: 'bar',
        fontFamily: 'Inter, sans-serif',
        toolbar: { show: false }
    },
    colors: ['#FF6B35'],
    plotOptions: {
        bar: {
            borderRadius: 8,
            columnWidth: '60%'
        }
    },
    xaxis: {
        categories: [
            t('analyticsDialog.months.jan'),
            t('analyticsDialog.months.feb'),
            t('analyticsDialog.months.mar'),
            t('analyticsDialog.months.apr'),
            t('analyticsDialog.months.may'),
            t('analyticsDialog.months.jun')
        ]
    },
    yaxis: {
        title: { text: t('analyticsDialog.chartLabels.energyKwh') }
    },
    grid: { borderColor: '#f3f4f6' }
}));

// Space Utilization Chart
const spaceUtilizationSeries = ref([{
    name: t('analyticsDialog.chartLabels.utilizationPercent'),
    data: [88, 75, 45, 82, 91]
}]);

const spaceUtilizationOptions = computed(() => ({
    chart: {
        type: 'bar',
        fontFamily: 'Inter, sans-serif',
        toolbar: { show: false }
    },
    colors: ['#10b981'],
    plotOptions: {
        bar: {
            borderRadius: 8,
            horizontal: true,
            barHeight: '70%'
        }
    },
    xaxis: {
        categories: [
            t('analyticsDialog.floors.floor1'),
            t('analyticsDialog.floors.floor2'),
            t('analyticsDialog.floors.floor3'),
            t('analyticsDialog.floors.floor4'),
            t('analyticsDialog.floors.floor5')
        ]
    },
    yaxis: {
        title: { text: t('analyticsDialog.chartLabels.utilizationPercent') }
    },
    grid: { borderColor: '#f3f4f6' }
}));

// Peak Hours Chart
const peakHoursSeries = ref([{
    name: t('analyticsDialog.chartLabels.people'),
    data: [45, 52, 78, 145, 234, 312, 389, 456, 512, 498, 467, 423, 378, 312, 267, 234, 189, 145, 98, 76, 54, 43, 38, 32]
}]);

const peakHoursOptions = computed(() => ({
    chart: {
        type: 'area',
        fontFamily: 'Inter, sans-serif',
        toolbar: { show: false }
    },
    colors: ['#8b5cf6'],
    stroke: {
        curve: 'smooth',
        width: 2
    },
    fill: {
        type: 'gradient',
        gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.6,
            opacityTo: 0.1
        }
    },
    xaxis: {
        categories: Array.from({ length: 24 }, (_, i) => `${i}:00`),
        title: { text: t('analyticsDialog.chartLabels.hour') }
    },
    yaxis: {
        title: { text: t('analyticsDialog.chartLabels.people') }
    },
    grid: { borderColor: '#f3f4f6' }
}));

// Handle window resize
function handleResize() {
    // Trigger chart resize if needed
    // You can add any resize logic here
}

// Lifecycle hooks
onMounted(() => {
    window.addEventListener('resize', handleResize);
});

onBeforeUnmount(() => {
    window.removeEventListener('resize', handleResize);
});

// Methods
function open() {
    visible.value = true;
}

function closeDialog() {
    visible.value = false;
}

// Expose methods
defineExpose({ open, close: closeDialog });
</script>
<style scoped>
/* Card Base */
.analytics-card {
    background: linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%);
    min-height: 100vh;
    min-height: 100dvh;
    position: relative;
}

/* Header */
.card-header {
    background: linear-gradient(135deg, #0A1929 0%, #1A2F42 100%);
    padding: 32px 40px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    position: sticky;
    top: 0;
    z-index: 100;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.left-section {
    flex: 1;
}

.header-badge {
    display: inline-block;
    padding: 6px 16px;
    background: rgba(30, 90, 142, 0.2);
    border: 1px solid rgba(30, 90, 142, 0.3);
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1px;
    color: #60a5fa;
    margin-bottom: 16px;
}

.card-header h2 {
    font-size: 32px;
    font-weight: 800;
    color: #ffffff;
    margin: 0 0 8px 0;
    letter-spacing: -0.5px;
}

.header-description {
    font-size: 15px;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.5;
    margin: 0;
}

.right-section {
    margin-left: 24px;
}

.dialog-close-btn {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
}

.dialog-close-btn:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.3);
    transform: scale(1.05);
}

.dialog-close-btn .v-icon {
    color: white;
}

/* Main Container */
.analytics-container {
    padding: 40px;
    max-width: 1600px;
    margin: 0 auto;
}

/* Metrics Grid */
.metrics-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 24px;
    margin-bottom: 48px;
}

.metric-card {
    background: white;
    border-radius: 20px;
    padding: 24px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
    border: 1px solid rgba(0, 0, 0, 0.04);
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.metric-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
}

.metric-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
}

.metric-icon {
    width: 56px;
    height: 56px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.3s ease;
}

.metric-card:hover .metric-icon {
    transform: scale(1.1) rotate(-5deg);
}

.metric-card.energy .metric-icon {
    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
}

.metric-card.occupancy .metric-icon {
    background: linear-gradient(135deg, #1E5A8E 0%, #2B7BC4 100%);
}

.metric-card.utilization .metric-icon {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.metric-card.satisfaction .metric-icon {
    background: linear-gradient(135deg, #FF6B35 0%, #FFA07A 100%);
}

.metric-card.cost .metric-icon {
    background: linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%);
}

.metric-card.efficiency .metric-icon {
    background: linear-gradient(135deg, #06b6d4 0%, #22d3ee 100%);
}

.metric-badge {
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 700;
}

.metric-badge.increase {
    background: #ecfdf5;
    color: #059669;
}

.metric-badge.decrease {
    background: #fef3c7;
    color: #d97706;
}

.metric-body h4 {
    font-size: 14px;
    font-weight: 600;
    color: #6b7280;
    margin: 0 0 8px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.metric-value {
    font-size: 32px;
    font-weight: 800;
    color: #1f2937;
    line-height: 1;
    margin: 0 0 4px 0;
}

.metric-label {
    font-size: 13px;
    color: #9ca3af;
    margin: 0;
}

.metric-footer {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 2px solid #f3f4f6;
}

.trend-text {
    font-size: 12px;
    color: #4b5563;
    font-weight: 500;
}

/* Section Styling */
.categories-section,
.charts-section,
.insights-section,
.comparative-section {
    margin-bottom: 48px;
}

.section-header {
    text-align: center;
    margin-bottom: 40px;
}

.section-header h3 {
    font-size: 32px;
    font-weight: 800;
    color: #1f2937;
    margin: 0 0 12px 0;
    letter-spacing: -0.5px;
}

.section-header p {
    font-size: 16px;
    color: #6b7280;
    margin: 0;
}

/* Category Cards */
.category-card {
    background: white;
    border-radius: 20px;
    padding: 28px;
    height: 100%;
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    border: 2px solid transparent;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}

.category-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12);
}

.category-card.space-analytics:hover {
    border-color: #1E5A8E;
}

.category-card.energy-analytics:hover {
    border-color: #fbbf24;
}

.category-card.user-analytics:hover {
    border-color: #10b981;
}

.category-card.booking-analytics:hover {
    border-color: #FF6B35;
}

.category-card.environmental:hover {
    border-color: #059669;
}

.category-card.predictive:hover {
    border-color: #8b5cf6;
}

.category-icon {
    width: 72px;
    height: 72px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
    transition: transform 0.4s ease;
}

.category-card:hover .category-icon {
    transform: scale(1.1) rotate(5deg);
}

.category-card.space-analytics .category-icon {
    background: linear-gradient(135deg, #1E5A8E 0%, #2B7BC4 100%);
}

.category-card.energy-analytics .category-icon {
    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
}

.category-card.user-analytics .category-icon {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.category-card.booking-analytics .category-icon {
    background: linear-gradient(135deg, #FF6B35 0%, #FFA07A 100%);
}

.category-card.environmental .category-icon {
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
}

.category-card.predictive .category-icon {
    background: linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%);
}

.category-card h4 {
    font-size: 20px;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 12px 0;
}

.category-card p {
    font-size: 14px;
    color: #6b7280;
    line-height: 1.7;
    margin: 0 0 20px 0;
}

.category-features {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.feature-tag {
    padding: 6px 12px;
    background: #f3f4f6;
    color: #4b5563;
    font-size: 12px;
    font-weight: 600;
    border-radius: 20px;
    transition: all 0.3s ease;
}

.category-card:hover .feature-tag {
    background: #e5e7eb;
}

/* Chart Containers */
.chart-container {
    background: white;
    border-radius: 20px;
    padding: 32px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
    border: 1px solid rgba(0, 0, 0, 0.04);
    height: 100%;
}

.chart-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 24px;
}

.chart-header h4 {
    font-size: 20px;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 8px 0;
}

.chart-header p {
    font-size: 14px;
    color: #6b7280;
    margin: 0;
}

.chart-wrapper {
    margin: 20px 0;
}

.chart-legend-custom {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-top: 20px;
}

.legend-gradient {
    width: 120px;
    height: 12px;
    border-radius: 6px;
    background: linear-gradient(90deg, #E3F2FD 0%, #90CAF9 33%, #42A5F5 66%, #1E5A8E 100%);
}

.legend-label {
    font-size: 12px;
    color: #6b7280;
    font-weight: 500;
}

.chart-insights {
    margin-top: 24px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.insight {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px;
    border-radius: 8px;
    font-size: 13px;
}

.insight.success {
    background: #ecfdf5;
    color: #059669;
}

.insight.info {
    background: #eff6ff;
    color: #1E5A8E;
}

/* Insights Cards */
.insights-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 24px;
}

.insight-card {
    background: white;
    border-radius: 20px;
    padding: 28px;
    border-left: 4px solid;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
    transition: all 0.3s ease;
}

.insight-card:hover {
    transform: translateX(8px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.insight-card.priority-high {
    border-color: #ef4444;
}

.insight-card.priority-medium {
    border-color: #f59e0b;
}

.insight-card.priority-low {
    border-color: #3b82f6;
}

.insight-card.priority-success {
    border-color: #10b981;
}

.insight-priority {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 16px;
}

.priority-high .insight-priority {
    background: #fef2f2;
    color: #ef4444;
}

.priority-medium .insight-priority {
    background: #fef3c7;
    color: #f59e0b;
}

.priority-low .insight-priority {
    background: #eff6ff;
    color: #3b82f6;
}

.priority-success .insight-priority {
    background: #ecfdf5;
    color: #10b981;
}

.insight-card h4 {
    font-size: 18px;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 12px 0;
}

.insight-card p {
    font-size: 14px;
    color: #6b7280;
    line-height: 1.7;
    margin: 0 0 16px 0;
}

.insight-action {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 16px;
    border-top: 2px solid #f3f4f6;
}

.potential-savings {
    font-size: 13px;
    font-weight: 600;
    color: #10b981;
}

/* Comparative Analytics */
.comparison-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
    gap: 24px;
}

.comparison-card {
    background: white;
    border-radius: 20px;
    padding: 32px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
    border: 1px solid rgba(0, 0, 0, 0.04);
}

.comparison-card h4 {
    font-size: 20px;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 24px 0;
}

.comparison-bars {
    display: flex;
    flex-direction: column;
    gap: 28px;
}

.bar-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.bar-label {
    font-size: 13px;
    font-weight: 600;
    color: #4b5563;
    margin-bottom: 4px;
}

.bar-container {
    position: relative;
    height: 32px;
    background: #f3f4f6;
    border-radius: 8px;
    overflow: hidden;
}

.bar {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding-right: 12px;
    font-size: 12px;
    font-weight: 700;
    color: white;
    transition: width 1s ease;
}

.bar.yours {
    background: linear-gradient(90deg, #1E5A8E 0%, #2B7BC4 100%);
}

.bar.industry {
    background: linear-gradient(90deg, #9ca3af 0%, #6b7280 100%);
}

.comparison-legend {
    display: flex;
    gap: 24px;
    justify-content: center;
    margin-top: 24px;
    padding-top: 24px;
    border-top: 2px solid #f3f4f6;
}

.comparison-legend span {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #4b5563;
    font-weight: 500;
}

.legend-dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
}

.legend-dot.yours {
    background: linear-gradient(135deg, #1E5A8E 0%, #2B7BC4 100%);
}

.legend-dot.industry {
    background: linear-gradient(135deg, #9ca3af 0%, #6b7280 100%);
}

.quarter-comparison {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.quarter-metric {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px;
    background: #f9fafb;
    border-radius: 12px;
    transition: all 0.3s ease;
}

.quarter-metric:hover {
    background: #f3f4f6;
    transform: translateX(4px);
}

.quarter-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.quarter-icon.improvement {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    color: white;
}

.quarter-details {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
}

.quarter-label {
    font-size: 13px;
    font-weight: 600;
    color: #6b7280;
}

.quarter-value {
    font-size: 20px;
    font-weight: 800;
    color: #1f2937;
}

.quarter-percent {
    font-size: 12px;
    font-weight: 600;
    color: #10b981;
}

/* Responsive */
@media (max-width: 1024px) {
    .analytics-container {
        padding: 32px 24px;
    }

    .metrics-grid {
        grid-template-columns: repeat(2, 1fr);
    }

    .comparison-cards {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 768px) {
    .card-header {
        padding: 24px;
        flex-direction: column;
        gap: 20px;
    }

    .card-header h2 {
        font-size: 24px;
    }

    .header-description {
        font-size: 14px;
    }

    .right-section {
        margin-left: 0;
        align-self: flex-end;
    }

    .analytics-container {
        padding: 24px 16px;
    }

    .metrics-grid {
        grid-template-columns: 1fr;
        gap: 16px;
    }

    .section-header h3 {
        font-size: 24px;
    }

    .insights-grid {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 480px) {
    .card-header h2 {
        font-size: 20px;
    }

    .analytics-container {
        padding: 20px 12px;
    }

    .chart-container {
        padding: 20px;
    }
}

/* Scrollbar */
.analytics-container::-webkit-scrollbar {
    width: 8px;
}

.analytics-container::-webkit-scrollbar-track {
    background: #f3f4f6;
}

.analytics-container::-webkit-scrollbar-thumb {
    background: #9ca3af;
    border-radius: 4px;
}

.analytics-container::-webkit-scrollbar-thumb:hover {
    background: #6b7280;
}
</style>